import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  Autocomplete,
  Chip
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, ArrowBack, Close } from '@mui/icons-material';
import { format } from 'date-fns';
import { formatDateOnly, parseLocalDate } from '../utils/dateUtils';
import { packingSlipService } from '../services/packingSlipService';
import { orderService } from '../services/orderService';
import { formatCurrency } from '../components/CurrencyTextField';
import '../styles/print.css';
import PrintPage from '../components/print/PrintPage';
import ResizableHeaderCell from '../components/ResizableHeaderCell';
import { useResizableColumns } from '../hooks/useResizableColumns';

const PackingSlips = () => {
  const DEFAULT_COL_WIDTHS = { workOrder: 130, packingSlip: 140, customer: 200, invoice: 130, shipDate: 110, carrier: 130, tracking: 180, actions: 110 };
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const workOrderIdFromUrl = searchParams.get('work_order_id');
  const partIdFromUrl = searchParams.get('part_id');
  const [slips, setSlips] = useState([]);
  const [orders, setOrders] = useState([]);
  const [workOrderFilter, setWorkOrderFilter] = useState(workOrderIdFromUrl || '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSlip, setSelectedSlip] = useState(null);
  const [sortKey, setSortKey] = useState('packing_slip_number');
  const [sortDirection, setSortDirection] = useState('desc');
  const { colWidths, startResize } = useResizableColumns({
    defaultWidths: DEFAULT_COL_WIDTHS,
    storageKey: 'wm_packing_slips_col_widths',
    minWidths: { workOrder: 100, packingSlip: 100, customer: 120, invoice: 90, shipDate: 90, carrier: 100, tracking: 120, actions: 90 }
  });

  const loadSlips = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        limit: 100,
        ...(workOrderFilter ? { work_order_id: workOrderFilter } : {}),
        ...(partIdFromUrl ? { part_id: partIdFromUrl } : {})
      };
      const res = await packingSlipService.getAll(params);
      const list = res.data || [];
      setSlips(list);
      setSelectedSlip(list.length > 0 ? list[0] : null);
    } catch (err) {
      console.error('Load packing slips:', err);
      setError(err.response?.data?.message || 'Failed to load packing slips');
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    try {
      const res = await orderService.getAll({ limit: 500 });
      const list = res.data || [];
      // Sort by order_date desc (newest first)
      list.sort((a, b) => {
        const da = a.order_date ? (parseLocalDate(a.order_date)?.getTime() ?? 0) : 0;
        const db = b.order_date ? (parseLocalDate(b.order_date)?.getTime() ?? 0) : 0;
        return db - da;
      });
      setOrders(list);
    } catch (err) {
      console.error('Load orders:', err);
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  useEffect(() => {
    loadSlips();
  }, [workOrderFilter, partIdFromUrl]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this packing slip? Invoices are not deleted.')) return;
    try {
      await packingSlipService.delete(id);
      await loadSlips();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete');
    }
  };

  const allOption = { id: '', isAll: true };
  const workOrderOptions = [allOption, ...orders];
  const selectedOrder = workOrderFilter
    ? orders.find((o) => String(o.id) === workOrderFilter) ?? null
    : allOption;
  const getWorkOrderLabel = (o) => {
    if (!o || o.isAll) return 'All';
    const dateStr = o.order_date ? formatDateOnly(o.order_date, 'MM/dd') : '—';
    const customerName = o.company_name || [o.customer_first_name, o.customer_last_name].filter(Boolean).join(' ') || '—';
    const amount = formatCurrency(o.total_amount);
    const woNumber = o.work_order_identifier ?? o.work_order_number ?? o.id;
    return `[#${woNumber}] ${dateStr} ${customerName} ${amount}`;
  };

  const handleSort = (key) => {
    setSortDirection((prev) => (sortKey === key ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'));
    setSortKey(key);
  };

  const displayedSlips = useMemo(() => {
    const list = [...slips];
    const getter = (row) => {
      switch (sortKey) {
        case 'work_order_number': return row.work_order_identifier ?? row.work_order_number ?? '';
        case 'packing_slip_number': return row.identifier ?? row.packing_slip_number ?? '';
        case 'customer': return row.customer_name ?? '';
        case 'shipped_date': return row.shipped_date ? new Date(row.shipped_date).getTime() : 0;
        case 'carrier': return row.carrier ?? '';
        case 'tracking_number': return row.tracking_number ?? '';
        case 'invoice': return row.invoice_identifier ?? row.invoice_number ?? '';
        default: return '';
      }
    };
    const dir = sortDirection === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      const va = getter(a);
      const vb = getter(b);
      if (typeof va === 'number' && typeof vb === 'number') return dir * (va - vb);
      return dir * String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    });
    return list;
  }, [slips, sortKey, sortDirection]);

  return (
    <Box>
      <Box className="no-print-shipments">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        {workOrderIdFromUrl && (
          <IconButton size="small" onClick={() => navigate(`/work-orders/${workOrderIdFromUrl}`)} title="Back to order">
            <ArrowBack />
          </IconButton>
        )}
        <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          Shipments
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Selected Shipment Information Bar */}
      <Paper sx={{ p: 1.5, mb: 2, backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Work Order</Typography>
            <Typography variant="body2">
              {selectedSlip?.work_order_id ? (
                <Button
                  size="small"
                  variant="text"
                  sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                  onClick={() => navigate(`/work-orders/${selectedSlip.work_order_id}`)}
                >
                  {selectedSlip?.work_order_identifier ?? selectedSlip?.work_order_number ?? selectedSlip?.work_order_id}
                </Button>
              ) : (
                selectedSlip?.work_order_identifier ?? selectedSlip?.work_order_number ?? selectedSlip?.work_order_id ?? '—'
              )}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Packing Slip #</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {selectedSlip?.identifier ?? selectedSlip?.packing_slip_number ?? '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Invoice</Typography>
            <Typography variant="body2">
              {selectedSlip?.invoice_id ? (
                <Button
                  size="small"
                  variant="text"
                  sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                  onClick={() => navigate(`/customer-invoices/${selectedSlip.invoice_id}`)}
                >
                  {selectedSlip?.invoice_identifier ?? selectedSlip?.invoice_number ?? `#${selectedSlip.invoice_id}`}
                </Button>
              ) : (
                '—'
              )}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Customer</Typography>
            <Typography variant="body2">
              {selectedSlip?.customer_id ? (
                <Button
                  size="small"
                  variant="text"
                  sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                  onClick={() => navigate(`/customers/${selectedSlip.customer_id}`)}
                >
                  {selectedSlip?.customer_name || `#${selectedSlip.customer_id}`}
                </Button>
              ) : (
                selectedSlip?.customer_name ?? '—'
              )}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Ship Date</Typography>
            <Typography variant="body2">
              {formatDateOnly(selectedSlip?.shipped_date) || '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Date Generated</Typography>
            <Typography variant="body2">
              {formatDateOnly(selectedSlip?.created_at) || '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Carrier</Typography>
            <Typography variant="body2">
              {selectedSlip?.carrier ?? '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Tracking</Typography>
            <Typography variant="body2">
              {selectedSlip?.tracking_number ?? '—'}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => setSelectedSlip(null)}
            disabled={!selectedSlip}
            sx={{ ml: 'auto' }}
            title="Clear selection"
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </Paper>

      <Paper sx={{ p: 1, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Autocomplete
            size="small"
            sx={{ minWidth: 600 }}
            ListboxProps={{
              sx: {
                '& .MuiAutocomplete-option': {
                  py: 0.5,
                  minHeight: 'auto',
                  color: '#000000',
                },
              },
            }}
            options={workOrderOptions}
            value={selectedOrder}
            onChange={(_, newValue) => setWorkOrderFilter(newValue && !newValue.isAll ? String(newValue.id) : '')}
            getOptionLabel={getWorkOrderLabel}
            renderOption={(props, option) => (
              <li {...props}>
                <Box
                  component="span"
                  sx={{
                    color: '#000000 !important',
                    fontSize: '0.875rem',
                    display: 'block',
                    py: 0.25,
                    fontWeight: 500,
                  }}
                >
                  {getWorkOrderLabel(option)}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Work order" placeholder="All or search by date, customer, amount..." />
            )}
            isOptionEqualToValue={(opt, val) => (opt && val && opt.isAll && val.isAll) || (opt && val && opt.id === val.id)}
          />
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => navigate(workOrderFilter ? `/packing-slips/new?work_order_id=${workOrderFilter}` : '/packing-slips/new')}>
            New packing slip
          </Button>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <ResizableHeaderCell width={colWidths.workOrder} onResizeStart={(e) => startResize('workOrder', e)} sortable sortActive={sortKey === 'work_order_number'} sortDirection={sortDirection} onSort={() => handleSort('work_order_number')}>Work Order ID</ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.packingSlip} onResizeStart={(e) => startResize('packingSlip', e)} sortable sortActive={sortKey === 'packing_slip_number'} sortDirection={sortDirection} onSort={() => handleSort('packing_slip_number')}>Packing Slip ID</ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.invoice} onResizeStart={(e) => startResize('invoice', e)} sortable sortActive={sortKey === 'invoice'} sortDirection={sortDirection} onSort={() => handleSort('invoice')}>Invoice ID</ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.customer} onResizeStart={(e) => startResize('customer', e)} sortable sortActive={sortKey === 'customer'} sortDirection={sortDirection} onSort={() => handleSort('customer')}>Customer</ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.shipDate} onResizeStart={(e) => startResize('shipDate', e)} sortable sortActive={sortKey === 'shipped_date'} sortDirection={sortDirection} onSort={() => handleSort('shipped_date')}>Ship Date</ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.carrier} onResizeStart={(e) => startResize('carrier', e)} sortable sortActive={sortKey === 'carrier'} sortDirection={sortDirection} onSort={() => handleSort('carrier')}>Carrier</ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.tracking} onResizeStart={(e) => startResize('tracking', e)} sortable sortActive={sortKey === 'tracking_number'} sortDirection={sortDirection} onSort={() => handleSort('tracking_number')}>Tracking</ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.actions} onResizeStart={(e) => startResize('actions', e)} align="right">Actions</ResizableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} align="center"><CircularProgress /></TableCell></TableRow>
            ) : displayedSlips.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center">No packing slips</TableCell></TableRow>
            ) : (
              displayedSlips.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  selected={selectedSlip?.id === row.id}
                  onClick={() => setSelectedSlip(row)}
                  onDoubleClick={() => navigate(`/packing-slips/${row.id}`)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell sx={{ width: colWidths.workOrder }} onClick={(e) => e.stopPropagation()}>
                    <Button size="small" onClick={() => navigate(`/work-orders/${row.work_order_id}`)}>
                      {row.work_order_identifier ?? row.work_order_number ?? row.work_order_id}
                    </Button>
                  </TableCell>
                  <TableCell sx={{ width: colWidths.packingSlip, fontWeight: 600 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                      <span>{row.identifier ?? row.packing_slip_number ?? '—'}</span>
                      {row.shipment_purpose === 'vendor_outbound' && (
                        <Chip label="Vendor" size="small" color="secondary" sx={{ height: 20, fontSize: '0.65rem' }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: colWidths.invoice }} onClick={(e) => e.stopPropagation()}>
                    {row.invoice_id ? (
                      <Button size="small" onClick={() => navigate(`/customer-invoices/${row.invoice_id}`)}>
                        {row.invoice_identifier ?? row.invoice_number ?? `#${row.invoice_id}`}
                      </Button>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell sx={{ width: colWidths.customer }}>{row.customer_name ?? '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.shipDate }}>{formatDateOnly(row.shipped_date) || '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.carrier }}>{row.carrier ?? '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.tracking }}>{row.tracking_number ?? '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.actions }} align="right" onClick={(e) => e.stopPropagation()}>
                    <IconButton size="small" onClick={() => navigate(`/packing-slips/${row.id}`)} title="Edit">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(row.id)} title="Delete">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      </Box>

      {/* Print view – shipments list */}
      <PrintPage title="Shipments" showSpacer={false}>
        <p style={{ fontSize: 12, marginBottom: 8 }}>
          {slips.length} record(s). Printed {format(new Date(), 'MM/dd/yyyy HH:mm')}
        </p>
        <table className="print-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ padding: 6, textAlign: 'left' }}>Work Order</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Packing Slip #</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Invoice ID</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Customer</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Ship Date</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Carrier</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Tracking</th>
            </tr>
          </thead>
          <tbody>
            {slips.length > 0 ? (
              slips.map((row) => (
                <tr key={row.id}>
                  <td style={{ padding: 6 }}>{row.work_order_identifier ?? row.work_order_number ?? row.work_order_id ?? '—'}</td>
                  <td style={{ padding: 6 }}>{row.identifier ?? row.packing_slip_number ?? '—'}</td>
                  <td style={{ padding: 6 }}>
                    {row.invoice_id
                      ? (row.invoice_identifier ?? row.invoice_number ?? `#${row.invoice_id}`)
                      : '—'}
                  </td>
                  <td style={{ padding: 6 }}>{row.customer_name ?? '—'}</td>
                  <td style={{ padding: 6 }}>
                    {formatDateOnly(row.shipped_date) || '—'}
                  </td>
                  <td style={{ padding: 6 }}>{row.carrier ?? '—'}</td>
                  <td style={{ padding: 6 }}>{row.tracking_number ?? '—'}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} style={{ padding: 6, textAlign: 'center' }}>No packing slips</td>
              </tr>
            )}
          </tbody>
        </table>
      </PrintPage>
    </Box>
  );
};

export default PackingSlips;
